import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
  Avatar,
  Box,
  Checkbox,
  MenuItem,
  Paper,
  Popper,
  styled,
} from '@mui/material'
import InputBase from '@mui/material/InputBase'
import { CustomCellEditorProps } from 'ag-grid-react'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import SaveButton from '../../../../components/buttons/SaveButton'
import CancelButton from '../../../../components/buttons/CancelButton'
import { MultiLineTooltip } from '../../../../components/tooltips/Tooltip/MultiLineTooltip'

export interface SelectOption {
  value: string
  label: string
  iconUrl?: string
  tooltip?: string
  disabled?: Boolean
}

interface Props extends CustomCellEditorProps {
  options: SelectOption[]
  value: string[]
}

export const MultiSelectCellEditor = (props: Props) => {
  const inputRef = React.createRef<HTMLInputElement>()
  const [value, setValue] = useState<SelectOption[]>([])

  useEffect(() => {
    setValue(
      props.value
        ? props.options.filter(o => props.value.includes(o.value))
        : []
    )
  }, [props.options, props.value])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const valueChanged = useCallback(
    (
      event: React.ChangeEvent<{}>,
      option: SelectOption[] | null,
      reason: AutocompleteChangeReason
    ) => {
      const newValue = option || []
      setValue(newValue)
      props.onValueChange(newValue.map(v => v.value))
    },
    []
  )
  const onSubmit = useCallback(() => {
    props.api.stopEditing()
  }, [props.api])

  const onCancel = useCallback(() => {
    props.api.stopEditing(true)
  }, [props.api])

  const onClose = useCallback(
    (e, reason) => {
      if (['blur', 'toggleInput'].includes(reason)) {
        return
      }
      let cancel = false
      if (reason === 'escape') {
        cancel = true
      }
      props.api.stopEditing(cancel)
    },
    [props.api]
  )

  const CustomPopperComponent = useMemo(
    () => (popperProps: React.ComponentProps<typeof Popper>) => (
      <Popper {...popperProps} placement="bottom-start" />
    ),
    []
  )

  const CustomPaperComponent = useMemo(
    () => (paperProps: React.ComponentProps<typeof Paper>) => (
      <Paper {...paperProps}>
        {paperProps.children}
        <ButtonBox>
          <SaveButton onClick={onSubmit} />
          <CancelButton onClick={onCancel} />
        </ButtonBox>
      </Paper>
    ),
    [onSubmit, onCancel]
  )

  return (
    <Autocomplete
      open={true}
      multiple={true}
      options={props.options}
      getOptionLabel={option => option.label}
      renderOption={(props, option: SelectOption, { selected }) => {
        const menuItem = (
          <MultiSelectMenuItem {...props} disabled={Boolean(option.disabled)}>
            <MultiSelectMenuItemCheckbox selected={selected} />
            {option.iconUrl && (
              <MultiSelectMenuItemIcon iconUrl={option.iconUrl} />
            )}
            <MultiSelectMenuItemLabel label={option.label} />
          </MultiSelectMenuItem>
        )

        if (option.tooltip) {
          return (
            <MultiLineTooltip message={option.tooltip}>
              {/* disabled=trueのオプションにもツールチップを表示したいためspanでラップ */}
              <span>{menuItem}</span>
            </MultiLineTooltip>
          )
        }

        return menuItem
      }}
      isOptionEqualToValue={(value, option) => value.value === option.value}
      // filterOptions={options => options}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <InputBase
          inputRef={inputRef}
          ref={params.InputProps.ref}
          inputProps={params.inputProps}
          autoFocus={true}
        />
      )}
      PopperComponent={CustomPopperComponent}
      PaperComponent={CustomPaperComponent}
      autoSelect={false}
      blurOnSelect={false}
      noOptionsText={''}
      value={value}
      onChange={valueChanged}
      onClose={onClose}
      disableCloseOnSelect={true}
    />
  )
}

const MultiSelectMenuItem = styled(MenuItem)({
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
  '& .MuiCheckbox-root': { marginRight: '0px !important' },
  '& .MuiAvatar-root': { margin: '0px 6px 0px 0px !important' },
})
const MultiSelectMenuItemCheckbox = ({
  selected,
  disabled,
}: {
  selected: boolean
  disabled?: boolean
}) => {
  return (
    <Checkbox
      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
      checkedIcon={<CheckBoxIcon fontSize="small" />}
      style={{ marginRight: 8 }}
      checked={selected}
      color={'primary'}
      disabled={disabled}
    />
  )
}
const MultiSelectMenuItemIcon = ({ iconUrl }: { iconUrl: string }) => {
  return (
    <Avatar
      variant="circular"
      sx={{
        marginRight: '5px',
        height: '23px',
        width: '23px',
        borderRadius: '50%',
      }}
      src={iconUrl}
    />
  )
}
const MultiSelectMenuItemLabel = ({ label }: { label: string }) => {
  return <Box sx={{ whiteSpace: 'nowrap' }}>{label}</Box>
}
const ButtonBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '36px',
})
